/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-03
 */

import { Box, Typography, Paper } from '@mui/material';
import { Business as StudioIcon } from '@mui/icons-material';
import { Studio, StudioRole, SubscriptionStatus } from '../../../types/studio';
import { StudioListCard } from '../../studios/StudioListCard';

interface StudiosGridProps {
  studios: Studio[];
  onStudioSelect: (studioId: string) => void;
  loading?: boolean;
}

/**
 * Simple grid component to display user's studios
 */
export const StudiosGrid: React.FC<StudiosGridProps> = ({
  studios,
  onStudioSelect,
  loading = false,
}) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 3,
        }}
      >
        {Array.from({ length: 4 }, (_, index) => (
          <Box key={`skeleton-${index}`}>
            <StudioListCard studio={{} as any} onView={() => {}} loading />
          </Box>
        ))}
      </Box>
    );
  }

  if (studios.length === 0) {
    return (
      <Paper
        sx={{
          p: 6,
          textAlign: 'center',
          bgcolor: 'background.default',
          border: '2px dashed',
          borderColor: 'divider',
        }}
      >
        <StudioIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" gutterBottom color="text.secondary">
          No studios found
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You don&apos;t belong to any studios yet.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)',
        },
        gap: 3,
      }}
    >
      {studios.map(studio => (
        <Box key={studio.id}>
          <StudioListCard
            studio={{
              id: studio.id,
              name: studio.name,
              description: studio.description,
              logo: studio.logo,
              memberCount: (studio as any).member_count || 0, // Map from snake_case API response
              userRole: (studio as any).userRole || StudioRole.MEMBER, // Use actual user role from membership data
              subscriptionStatus:
                studio.subscriptionStatus || SubscriptionStatus.ACTIVE,
              lastActivity: (studio as any).updated_at || studio.createdAt, // Map from snake_case API response
              createdAt: (studio as any).created_at || studio.createdAt, // Map from snake_case API response
              isPersonal:
                (studio as any).is_personal || studio.isPersonal || false, // Map from snake_case API response
            }}
            onView={onStudioSelect}
            onEdit={() => {}} // TODO: Implement edit functionality
            onSettings={() => {}} // TODO: Implement settings functionality
          />
        </Box>
      ))}
    </Box>
  );
};
